import React from 'react';

function EnvTest() {
  // Check for critical environment variables
  const checkEnv = () => {
    const envVars = {
      'REACT_APP_GOOGLE_CLIENT_ID': process.env.REACT_APP_GOOGLE_CLIENT_ID,
      'REACT_APP_GOOGLE_CLIENT_SECRET': process.env.REACT_APP_GOOGLE_CLIENT_SECRET
    };
    
    // Mask sensitive data for display (show only first few chars)
    const maskedVars = Object.keys(envVars).reduce((acc, key) => {
      const value = envVars[key];
      if (!value) {
        acc[key] = { value: null, status: 'Missing', className: 'text-red-600' };
      } else if (value.length < 10) {
        acc[key] = { value: 'Too short (possible error)', status: 'Invalid', className: 'text-red-600' };
      } else {
        // Mask value to show only first 5 characters
        const masked = value.substring(0, 5) + '...' + value.substring(value.length - 3);
        acc[key] = { value: masked, status: 'Present', className: 'text-green-600' };
      }
      return acc;
    }, {});
    
    return maskedVars;
  };
  
  const envStatus = checkEnv();
  
  // Function to check if the OAuth redirect URI is properly configured
  const checkRedirectUri = () => {
    const currentOrigin = window.location.origin;
    const redirectUri = `${currentOrigin}/auth/google-calendar-callback`;
    
    return {
      value: redirectUri,
      status: 'Make sure this URI is added to Google Cloud Console',
      className: 'text-blue-600'
    };
  };
  
  const redirectUriStatus = checkRedirectUri();
  
  // Function to test localStorage and sessionStorage with the same values that would be used in OAuth
  const testStorage = () => {
    const testKey = 'googleOAuthState';
    const testValue = Math.random().toString(36).substring(2, 12);
    
    // Test results for both storage types
    const results = {
      localStorage: { status: 'Not tested', className: 'text-gray-500' },
      sessionStorage: { status: 'Not tested', className: 'text-gray-500' }
    };
    
    // Test localStorage
    try {
      localStorage.setItem(testKey, testValue);
      const readLocalValue = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      
      if (readLocalValue === testValue) {
        results.localStorage = { status: 'Working Correctly', className: 'text-green-600' };
      } else {
        results.localStorage = { 
          status: 'Error: Value mismatch', 
          className: 'text-red-600',
          details: `Expected: ${testValue}, Got: ${readLocalValue}`
        };
      }
    } catch (error) {
      results.localStorage = { 
        status: 'Error: ' + error.message, 
        className: 'text-red-600' 
      };
    }
    
    // Test sessionStorage
    try {
      sessionStorage.setItem(testKey, testValue);
      const readSessionValue = sessionStorage.getItem(testKey);
      sessionStorage.removeItem(testKey);
      
      if (readSessionValue === testValue) {
        results.sessionStorage = { status: 'Working Correctly', className: 'text-green-600' };
      } else {
        results.sessionStorage = { 
          status: 'Error: Value mismatch', 
          className: 'text-red-600',
          details: `Expected: ${testValue}, Got: ${readSessionValue}`
        };
      }
    } catch (error) {
      results.sessionStorage = { 
        status: 'Error: ' + error.message, 
        className: 'text-red-600' 
      };
    }
    
    return results;
  };
  
  const storageStatus = testStorage();
  
  // Function to test a manual OAuth state setup
  const testOauthState = () => {
    const stateValue = Math.random().toString(36).substring(2, 12);
    
    try {
      // Store in both storage types
      localStorage.setItem('googleOAuthState', stateValue);
      sessionStorage.setItem('googleOAuthState', stateValue);
      
      return {
        state: stateValue,
        status: 'Test state stored in both localStorage and sessionStorage',
        className: 'text-green-600'
      };
    } catch (error) {
      return {
        state: null,
        status: 'Failed to store test state: ' + error.message,
        className: 'text-red-600'
      };
    }
  };
  
  const oauthTestState = testOauthState();
  
  // Function to generate a test OAuth URL
  const generateTestOAuthUrl = () => {
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        return {
          url: null,
          status: 'Cannot generate URL: Client ID not available',
          className: 'text-red-600'
        };
      }
      
      const redirectUri = `${window.location.origin}/auth/google-calendar-callback`;
      const scopes = [
        'https://www.googleapis.com/auth/calendar.readonly',
        'https://www.googleapis.com/auth/calendar.events.readonly'
      ];
      
      const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      url.searchParams.append('client_id', clientId);
      url.searchParams.append('redirect_uri', redirectUri);
      url.searchParams.append('response_type', 'code');
      url.searchParams.append('scope', scopes.join(' '));
      url.searchParams.append('access_type', 'offline');
      url.searchParams.append('state', oauthTestState.state);
      url.searchParams.append('prompt', 'consent');
      
      return {
        url: url.toString(),
        status: 'Test URL generated',
        className: 'text-green-600'
      };
    } catch (error) {
      return {
        url: null,
        status: 'Failed to generate URL: ' + error.message,
        className: 'text-red-600'
      };
    }
  };
  
  const oauthUrl = generateTestOAuthUrl();

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Environment Configuration Test</h1>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Google OAuth Credentials</h2>
        <div className="bg-gray-100 p-4 rounded-md">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-2">Variable</th>
                <th className="text-left py-2">Value</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(envStatus).map(key => (
                <tr key={key} className="border-b border-gray-200">
                  <td className="py-2 font-mono text-sm">{key}</td>
                  <td className="py-2 font-mono text-sm">{envStatus[key].value || 'Not set'}</td>
                  <td className={`py-2 font-semibold ${envStatus[key].className}`}>
                    {envStatus[key].status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Redirect URI</h2>
        <div className="bg-gray-100 p-4 rounded-md">
          <p className="mb-2 font-mono text-sm break-all">{redirectUriStatus.value}</p>
          <p className={`text-sm ${redirectUriStatus.className}`}>{redirectUriStatus.status}</p>
        </div>
      </div>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Browser Storage Test</h2>
        <div className="bg-gray-100 p-4 rounded-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-base font-medium mb-2">Local Storage</h3>
              <p className={`font-semibold ${storageStatus.localStorage.className}`}>
                {storageStatus.localStorage.status}
              </p>
              {storageStatus.localStorage.details && (
                <p className="text-sm mt-1">{storageStatus.localStorage.details}</p>
              )}
            </div>
            <div>
              <h3 className="text-base font-medium mb-2">Session Storage</h3>
              <p className={`font-semibold ${storageStatus.sessionStorage.className}`}>
                {storageStatus.sessionStorage.status}
              </p>
              {storageStatus.sessionStorage.details && (
                <p className="text-sm mt-1">{storageStatus.sessionStorage.details}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Manual OAuth Test</h2>
        <div className="bg-gray-100 p-4 rounded-md">
          <div className="mb-4">
            <h3 className="text-base font-medium mb-2">Test State</h3>
            <p className={`mb-1 ${oauthTestState.className}`}>{oauthTestState.status}</p>
            {oauthTestState.state && (
              <div className="bg-white p-2 rounded font-mono text-sm mb-3">
                {oauthTestState.state}
              </div>
            )}
          </div>
          
          {oauthUrl.url ? (
            <div>
              <h3 className="text-base font-medium mb-2">Test OAuth URL</h3>
              <p className="mb-2 text-sm">Click the link below to test the OAuth flow manually:</p>
              <div className="mb-3">
                <a 
                  href={oauthUrl.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Test OAuth Flow
                </a>
              </div>
              <div className="bg-white p-2 rounded overflow-x-auto">
                <p className="font-mono text-xs break-all">{oauthUrl.url}</p>
              </div>
            </div>
          ) : (
            <div className="text-red-600">{oauthUrl.status}</div>
          )}
        </div>
      </div>
      
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <h3 className="text-lg font-semibold text-yellow-800">Troubleshooting Tips</h3>
        <ul className="mt-2 list-disc list-inside text-yellow-700">
          <li>Make sure you've added the exact redirect URI to Google Cloud Console's OAuth settings</li>
          <li>Check that you've enabled the Google Calendar API in your Google Cloud project</li>
          <li>In Google Cloud Console, verify your OAuth consent screen is properly configured</li>
          <li>Try using an incognito/private browsing window to avoid cookie/storage issues</li>
          <li>Check browser console for any errors during the OAuth flow</li>
          <li>After making any changes to Google Cloud settings, wait a few minutes for them to propagate</li>
        </ul>
      </div>
    </div>
  );
}

export default EnvTest; 